/**
 * USE PAGE SETTINGS HOOK
 * =======================
 * 
 * Hook pour gérer les paramètres de pages (Control Plane).
 * Gère: visibilité retailer, ordre d'affichage, activation CMS HUB.
 * 
 * Stockage: Collection MongoDB page_settings
 * 
 * SAFE MODE: Ne modifie pas les routes, uniquement les métadonnées d'affichage.
 */

import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * Hook pour charger et gérer les settings d'une page
 */
export const usePageSettings = (pageId) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadSettings = useCallback(async () => {
    if (!pageId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.get(`/page-settings/${pageId}`);
      setSettings(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading page settings:', err);
      setError(err.message);
      // Set default settings on error
      setSettings({
        page_id: pageId,
        is_published_retailer: true,
        display_order: 99,
        hub_cms_enabled: true
      });
    } finally {
      setLoading(false);
    }
  }, [pageId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const updateSettings = async (updates) => {
    try {
      const response = await api.put(`/page-settings/${pageId}`, updates);
      setSettings(response.data);
      return response.data;
    } catch (err) {
      console.error('Error updating page settings:', err);
      throw err;
    }
  };

  const toggleRetailerVisibility = async () => {
    const newValue = !settings?.is_published_retailer;
    const updated = await updateSettings({ is_published_retailer: newValue });
    toast.success(newValue ? 'Page publiée pour les magasins' : 'Page masquée pour les magasins');
    return updated;
  };

  const setDisplayOrder = async (order) => {
    const updated = await updateSettings({ display_order: order });
    return updated;
  };

  const toggleHubCms = async () => {
    const newValue = !settings?.hub_cms_enabled;
    const updated = await updateSettings({ hub_cms_enabled: newValue });
    toast.success(newValue ? 'Édition CMS activée' : 'Édition CMS désactivée');
    return updated;
  };

  return {
    settings,
    loading,
    error,
    updateSettings,
    toggleRetailerVisibility,
    setDisplayOrder,
    toggleHubCms,
    reload: loadSettings
  };
};

/**
 * Hook pour charger tous les settings (Admin Control Plane)
 */
export const useAllPageSettings = () => {
  const [settingsMap, setSettingsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAllSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/page-settings');
      
      // Convert array to map by page_id
      const map = {};
      response.data.forEach(setting => {
        map[setting.page_id] = setting;
      });
      setSettingsMap(map);
      setError(null);
    } catch (err) {
      console.error('Error loading all page settings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllSettings();
  }, [loadAllSettings]);

  const getSettingsForPage = (pageId) => {
    return settingsMap[pageId] || {
      page_id: pageId,
      is_published_retailer: true,
      display_order: 99,
      hub_cms_enabled: true
    };
  };

  const updatePageSettings = async (pageId, updates) => {
    try {
      const response = await api.put(`/page-settings/${pageId}`, updates);
      setSettingsMap(prev => ({
        ...prev,
        [pageId]: response.data
      }));
      return response.data;
    } catch (err) {
      console.error('Error updating page settings:', err);
      throw err;
    }
  };

  const bulkUpdateOrder = async (orderUpdates) => {
    try {
      // orderUpdates: [{page_id: "...", display_order: 1}, ...]
      const response = await api.post('/page-settings/bulk', orderUpdates);
      
      // Update local state
      const newMap = { ...settingsMap };
      orderUpdates.forEach(update => {
        if (newMap[update.page_id]) {
          newMap[update.page_id] = {
            ...newMap[update.page_id],
            display_order: update.display_order
          };
        } else {
          newMap[update.page_id] = {
            page_id: update.page_id,
            is_published_retailer: true,
            display_order: update.display_order,
            hub_cms_enabled: true
          };
        }
      });
      setSettingsMap(newMap);
      
      toast.success('Ordre mis à jour');
      return response.data;
    } catch (err) {
      console.error('Error bulk updating order:', err);
      toast.error('Erreur lors de la mise à jour de l\'ordre');
      throw err;
    }
  };

  const toggleRetailerVisibility = async (pageId) => {
    const current = getSettingsForPage(pageId);
    const newValue = !current.is_published_retailer;
    
    const updated = await updatePageSettings(pageId, { is_published_retailer: newValue });
    toast.success(newValue ? 'Page publiée' : 'Page masquée');
    return updated;
  };

  return {
    settingsMap,
    loading,
    error,
    getSettingsForPage,
    updatePageSettings,
    bulkUpdateOrder,
    toggleRetailerVisibility,
    reload: loadAllSettings
  };
};

/**
 * Hook pour le menu retailer avec filtrage par visibilité
 */
export const useRetailerVisiblePages = () => {
  const [unpublishedIds, setUnpublishedIds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUnpublished = async () => {
      try {
        const response = await api.get('/page-settings/retailer-visible');
        setUnpublishedIds(response.data.unpublished_page_ids || []);
      } catch (err) {
        console.error('Error loading retailer visibility:', err);
        setUnpublishedIds([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadUnpublished();
  }, []);

  const isPageVisible = (pageId) => {
    return !unpublishedIds.includes(pageId);
  };

  return {
    unpublishedIds,
    loading,
    isPageVisible
  };
};

export default {
  usePageSettings,
  useAllPageSettings,
  useRetailerVisiblePages
};
